import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { lookupMarianaTekUser, lookupBarrysPlatformUser, lookupShopifyCustomer, getMarianaTekMemberships, getMarianaTekCreditTransactions, getMarianaTekReservations, getExtolePersonByEmail, getExtoleShareables } from '../services/api'
import { useAuth } from './auth/AuthProvider'
import UserDataCache from '../services/userDataCache'
import UserTagManagement from './UserTagManagement'
import UserNotesModal from './UserNotesModal'

// Tag ID to icon mapping
const TAG_MAPPING: Record<number, { icon: string; description: string }> = {
  1: { icon: 'icon-employee.svg', description: 'Employee - Personal Account' },
  3: { icon: 'icon-high-maintenance.svg', description: 'Extra Care' },
  4: { icon: 'icon-medical.svg', description: 'Injury' },
  5: { icon: 'icon-military.svg', description: 'Military' },
  6: { icon: 'icon-student.svg', description: 'Student' },
  11: { icon: 'icon-corporate.svg', description: 'HQ Employee' },
  12: { icon: 'icon-originals.svg', description: 'Originals' },
  13: { icon: 'Mariana-Tag_RreU1d1.svg', description: 'Mariana Tek Employee' },
  14: { icon: 'Partner.svg', description: 'Partner' },
  15: { icon: 'Notes.svg', description: 'Notes' },
  16: { icon: 'first-responder2.svg', description: 'First Responder' },
  17: { icon: 'waiver_tag_vuilOfY.svg', description: 'Needs COVID-19 Waiver' },
  50: { icon: 'All_Star2x.svg', description: 'Silver' },
  51: { icon: 'cancellation.svg', description: 'Cancellation Forgiveness' },
  52: { icon: 'fuel_1_1.svg', description: 'Free Fuel Item' },
  53: { icon: 'Legend2x.svg', description: 'Black' },
  54: { icon: 'member.svg', description: 'Member' },
  55: { icon: 'Player2x.svg', description: 'Red' },
  56: { icon: 'LoyaltyVIP2x.svg', description: 'Platinum' },
  83: { icon: 'Tag_Soho_House_BVcPkjE.svg', description: 'Soho House' },
  84: { icon: 'tag_vaccinated_6iRe5PO.svg', description: 'Vaccinated' },
  149: { icon: '5_Off_Fuel_1.svg', description: '5% Fuel Item Discount' },
  150: { icon: '10_Off_Fuel_1_1_1.svg', description: '10% Fuel Item Discount' },
  151: { icon: 'Early_Booking.svg', description: 'Early Booking' },
  152: { icon: 'Priority_Waitlist.svg', description: 'Priority Waitlist' },
  182: { icon: 'barrys_corp_5.svg', description: '5% Corporate UK' },
  183: { icon: 'barrys_corp_10.svg', description: '10% Corporate UK' },
  184: { icon: 'barrys_corp_15.svg', description: '15% Corporate UK' },
  185: { icon: 'barrys_corp_20.svg', description: '20% Corporate UK' },
  215: { icon: 'exclusive_member_barrystag.svg', description: 'Exclusive Member' },
  248: { icon: 'barrus_u27.svg', description: 'Fuel Client Info' },
  281: { icon: 'Needs_Guardian_Tag2x.svg', description: 'Needs Guardian' },
  315: { icon: 'Barrys_hellweekfinisher.svg', description: 'Hell Week Finisher' },
  348: { icon: '6_Month_Member_Tag.svg', description: '6 Month Member' },
  349: { icon: 'first_timer.png', description: 'First Timer Referred Friend' }
}

const ProfileContainer = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  margin-bottom: 2rem;
`

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
`

const ProfileField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled.label`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const Value = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.base};
  font-weight: 500;
`

const ValueWithCopy = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CopyButton = styled.button`
  background: transparent;
  color: #999;
  border: none;
  padding: 0.3rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #666;
    background: #f5f5f5;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: ${theme.color.background.light};
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  min-height: 300px;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.color.border.light};
  border-top: 4px solid ${theme.color.accent.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const LoadingText = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  text-align: center;
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
`

const AddressContainer = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 1rem;
  align-items: start;
`

const AddressValue = styled(Value)`
  white-space: pre-line;
`

const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
`

const ProfileHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const TagsAndClassesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const ClassesTaken = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  padding: 0.5rem 1rem;
  background: ${theme.color.background.frame};
  border-radius: 8px;
  border: 2px solid ${theme.color.accent.primary};
`

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  max-width: 300px;
`

const TagIcon = styled.img`
  width: 32px;
  height: 32px;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`

const Tooltip = styled.div<{ show: boolean }>`
  position: absolute;
  background: #333;
  color: white;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  white-space: nowrap;
  z-index: 1000;
  transform: translateX(-50%);
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #333;
  }
`

const TagIconWrapper = styled.div`
  position: relative;
  display: inline-block;
`

const ProfileLinksContainer = styled.div`
  background: ${theme.color.background.frame};
  padding: 0.65rem;
  border-radius: 5px;
  border: 1px solid ${theme.color.border.light};
  width: 260px;
  flex-shrink: 0;
`

const ProfileLinksTitle = styled.h3`
  font-size: 1.04rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const MembershipsSection = styled.div`
  grid-column: 1 / -1;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid ${theme.color.border.light};
`

const MembershipsTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
`

const MembershipsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`

const MembershipCard = styled.div`
  background: ${theme.color.background.frame};
  border: 2px solid ${theme.color.accent.primary};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px ${theme.color.shadow.light};
`

const MembershipName = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
`

const MembershipDetail = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 1.3rem;
`

const MembershipLabel = styled.span`
  color: ${theme.color.text.grey};
  font-weight: 600;
`

const MembershipValue = styled.span`
  color: ${theme.color.text.base};
  font-weight: 500;
`

const NoMemberships = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-style: italic;
`

const PackagesSection = styled.div`
  grid-column: 1 / -1;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid ${theme.color.border.light};
`

const PackagesTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
`

const PackagesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`

const PackageCard = styled.div`
  background: ${theme.color.background.frame};
  border: 2px solid ${theme.color.accent.primary};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px ${theme.color.shadow.light};
`

const PackageName = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
`

const PackageDetail = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 1.3rem;
`

const PackageLabel = styled.span`
  color: ${theme.color.text.grey};
  font-weight: 600;
`

const PackageValue = styled.span`
  color: ${theme.color.text.base};
  font-weight: 500;
`

const NoPackages = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-style: italic;
`

const SectionLoadingContainer = styled.div`
  display: flex;
  justify:center;
  align-items: center;
  padding: 3rem;
`

const SectionLoadingText = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
  }
`

const MergedProfilesSection = styled.div`
  grid-column: 1 / -1;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid ${theme.color.border.light};
`

const MergedProfilesTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
`

const BackupsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const BackupCard = styled.div`
  background: ${theme.color.background.frame};
  border: 2px solid #6c5ce7;
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const BackupInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const BackupEmail = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.color.text.base};
`

const BackupDate = styled.div`
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
`

const DownloadButton = styled.button`
  background: #6c5ce7;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #5b4cdb;
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${theme.color.border.light};
    cursor: not-allowed;
    transform: none;
  }
`

const NoBackups = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-style: italic;
`

const LinksGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.52rem;
`

const LinkButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  padding: 0.52rem 0.78rem;
  border-radius: 4px;
  font-size: 0.845rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  width: 100%;

  &:hover {
    background: #b8002d;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: ${theme.color.border.light};
    color: ${theme.color.text.grey};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`

const UserNotesButton = styled.button`
  width: 100%;
  background: #fefacd;
  color: #444;
  border: 2px solid #d4a017;
  padding: 0.52rem 0.78rem;
  border-radius: 4px;
  font-size: 0.845rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    background: #fef9b8;
    border-color: #b8860b;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(212, 160, 23, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`

interface UserProfileProps {
  userData: any
  refreshTrigger?: number // Optional prop to force refresh
  onLoadingStateChange?: (isLoading: boolean) => void // Callback to notify parent of loading state
  onExternalDataLoaded?: (marianaTekData: any, barrysPlatformData: any, reservationsData: any) => void // Callback to pass external data to parent
  onUpdate?: () => void // Callback to refresh user data
}

const UserProfile: React.FC<UserProfileProps> = ({ userData, refreshTrigger, onLoadingStateChange, onExternalDataLoaded, onUpdate }) => {
  const { isSuperAdmin, isAdmin } = useAuth()
  const [marianaTekData, setMarianaTekData] = useState<any>(null)
  const [barrysPlatformData, setBarrysPlatformData] = useState<any>(null)
  const [shopifyData, setShopifyData] = useState<any>(null)
  const [memberships, setMemberships] = useState<any[]>([])
  const [classPackages, setClassPackages] = useState<any[]>([])
  const [credits, setCredits] = useState<any[]>([])
  const [reservations, setReservations] = useState<any>(null)
  const [hoveredTag, setHoveredTag] = useState<number | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [showUserNotes, setShowUserNotes] = useState(false)
  const [referralCode, setReferralCode] = useState<string | null>(null)
  const [loadingStates, setLoadingStates] = useState({
    marianaTek: false,
    barrysPlatform: false,
    shopify: false,
    memberships: false,
    packages: false,
    reservations: false,
    extole: false
  })
  const [completedStates, setCompletedStates] = useState({
    marianaTek: false,
    barrysPlatform: false,
    shopify: false,
    memberships: false,
    packages: false,
    reservations: false,
    extole: false
  })
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const fetchingRef = React.useRef(false)
  const lastEmailRef = React.useRef<string | null>(null)
  const [mergedProfileBackups, setMergedProfileBackups] = useState<any[]>([])
  const [loadingBackups, setLoadingBackups] = useState(false)
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null)

  // Fetch profiles that were merged INTO this user
  useEffect(() => {
    const fetchMergedProfiles = async () => {
      if (!isSuperAdmin || !userData?.user?.external_id) {
        setMergedProfileBackups([])
        return
      }

      setLoadingBackups(true)
      try {
        // Use the merged-into endpoint to find profiles merged into this user
        const response = await fetch(`/api/mt-capture/merged-into/${userData.user.external_id}`)
        const data = await response.json()

        if (data.success && data.mergedProfiles && data.mergedProfiles.length > 0) {
          setMergedProfileBackups(data.mergedProfiles)
        } else {
          setMergedProfileBackups([])
        }
      } catch (error) {
        console.error('Error fetching merged profiles:', error)
        setMergedProfileBackups([])
      } finally {
        setLoadingBackups(false)
      }
    }

    fetchMergedProfiles()
  }, [isSuperAdmin, userData?.user?.external_id])

  const handleDownloadExcel = async (key: string) => {
    setDownloadingKey(key)
    try {
      const response = await fetch('/api/mt-capture/generate-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key })
      })

      if (!response.ok) {
        throw new Error('Failed to generate Excel file')
      }

      // Get the blob and download it
      const blob = await response.blob()
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = 'backup.xlsx'
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/)
        if (match) filename = match[1]
      }

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading Excel:', error)
      alert('Failed to download Excel file')
    } finally {
      setDownloadingKey(null)
    }
  }

  useEffect(() => {
    const fetchExternalData = async () => {
      // Check if no user data
      if (!userData?.user?.email) {
        console.log('No user email, skipping fetch')
        return
      }

      // Reset fetching flag if user changed
      const userChanged = lastEmailRef.current !== userData.user.email
      if (userChanged) {
        console.log('User changed, resetting fetch flag:', lastEmailRef.current, '->', userData.user.email)
        fetchingRef.current = false
        lastEmailRef.current = userData.user.email
      }

      // Prevent concurrent fetches - exit early if already fetching
      if (fetchingRef.current) {
        console.log('Already fetching data, ignoring duplicate request')
        return
      }

      // Start fetching
      console.log('Starting fetch for user:', userData.user.email, 'refreshTrigger:', refreshTrigger)
      fetchingRef.current = true
      setIsFetching(true)

      try {
        // Reset initial load state for new user
        setIsInitialLoad(true)

        const cache = UserDataCache.getInstance()

        // If refreshTrigger is provided, clear cache to force fresh data
        if (refreshTrigger) {
          console.log('Clearing cache for refresh')
          cache.clearUser(userData.user.email)
        }

        const cached = cache.get(userData.user.email)
        
        if (cached && !refreshTrigger) {
          // Use cached data only if not forcing refresh
          console.log('Using cached data for:', userData.user.email)
          setMarianaTekData(cached.marianaTekData)
          setBarrysPlatformData(cached.barrysPlatformData)
          setShopifyData(cached.shopifyData)
          setMemberships(cached.memberships || [])
          setClassPackages(cached.classPackages || [])
          setCredits(cached.credits || [])
          setReservations(cached.reservations || null)
          setReferralCode(cached.referralCode || null)
          setCompletedStates({
            marianaTek: true,
            barrysPlatform: true,
            shopify: true,
            memberships: true,
            packages: true,
            reservations: true,
            extole: true
          })
          setLoadingStates({
            marianaTek: false,
            barrysPlatform: false,
            shopify: false,
            memberships: false,
            packages: false,
            reservations: false,
            extole: false
          })

          // Notify parent of external data
          if (onExternalDataLoaded) {
            onExternalDataLoaded(cached.marianaTekData, cached.barrysPlatformData, cached.reservations)
          }

          setIsInitialLoad(false)
          setIsFetching(false)
          fetchingRef.current = false
          return
        }

        console.log('Fetching fresh data for:', userData.user.email)

        // Reset states for fresh fetch
        setCompletedStates({
          marianaTek: false,
          barrysPlatform: false,
          shopify: false,
          memberships: false,
          packages: false,
          reservations: false,
          extole: false
        })
        setLoadingStates({
          marianaTek: false,
          barrysPlatform: false,
          shopify: false,
          memberships: false,
          packages: false,
          reservations: false,
          extole: false
        })

        // Collect all the data
        let fetchedMarianaTekData = null
        let fetchedBarrysPlatformData = { found: false, id: null }
        let fetchedShopifyData = { found: false, id: null }
        let fetchedMemberships: any[] = []
        let fetchedClassPackages: any[] = []
        let fetchedReservations: any = null
        
        // Fetch fresh data with timeout
        setLoadingStates(prev => ({ ...prev, marianaTek: true }))
        try {
          // Fetch Mariana Tek data with 10 second timeout
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Mariana Tek lookup timeout')), 10000)
          )
          fetchedMarianaTekData = await Promise.race([
            lookupMarianaTekUser(userData.user.email),
            timeoutPromise
          ]) as any
          console.log('Mariana Tek data received:', fetchedMarianaTekData)
          setMarianaTekData(fetchedMarianaTekData)
        } catch (error) {
          console.error('Failed to fetch Mariana Tek data:', error)
          setMarianaTekData(null)
        } finally {
          setLoadingStates(prev => ({ ...prev, marianaTek: false }))
          setCompletedStates(prev => ({ ...prev, marianaTek: true }))
        }

        setLoadingStates(prev => ({ ...prev, barrysPlatform: true }))
        try {
          // Fetch Barry's Platform data with 10 second timeout
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Barry\'s Platform lookup timeout')), 10000)
          )
          fetchedBarrysPlatformData = await Promise.race([
            isSuperAdmin ? lookupBarrysPlatformUser(userData.user.email) : Promise.resolve({ found: false }),
            timeoutPromise
          ]) as any
          console.log('Barrys Platform data received:', fetchedBarrysPlatformData)
          setBarrysPlatformData(fetchedBarrysPlatformData)
        } catch (error) {
          console.error('Failed to fetch Barrys Platform data:', error)
          // Set a default state so the UI doesn't break
          setBarrysPlatformData(fetchedBarrysPlatformData)
        } finally {
          setLoadingStates(prev => ({ ...prev, barrysPlatform: false }))
          setCompletedStates(prev => ({ ...prev, barrysPlatform: true }))
        }

        setLoadingStates(prev => ({ ...prev, shopify: true }))
        try {
          // Fetch Shopify data with 10 second timeout
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Shopify lookup timeout')), 10000)
          )
          fetchedShopifyData = await Promise.race([
            isAdmin ? lookupShopifyCustomer(userData.user.email) : Promise.resolve({ found: false }),
            timeoutPromise
          ]) as any
          console.log('Shopify data received:', fetchedShopifyData)
          setShopifyData(fetchedShopifyData)
        } catch (error) {
          console.error('Failed to fetch Shopify data:', error)
          setShopifyData(fetchedShopifyData)
        } finally {
          setLoadingStates(prev => ({ ...prev, shopify: false }))
          setCompletedStates(prev => ({ ...prev, shopify: true }))
        }

        // Fetch memberships if we have a Mariana Tek user ID
        if (userData.user.external_id) {
          setLoadingStates(prev => ({ ...prev, memberships: true }))
          try {
            // Fetch memberships with 10 second timeout
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Memberships lookup timeout')), 10000)
            )
            const membershipsData = await Promise.race([
              getMarianaTekMemberships(userData.user.external_id),
              timeoutPromise
            ]) as any
            console.log('Memberships data received:', membershipsData)

            // Filter for active and frozen memberships
            const activeMemberships = (membershipsData.data || []).filter((membership: any) => {
              const status = membership.attributes.status
              const cancellationDate = membership.attributes.cancellation_datetime
              const endDate = membership.attributes.end_date
              const freezeDatetime = membership.attributes.freeze_datetime
              const now = new Date()

              console.log('Membership:', {
                id: membership.id,
                name: membership.attributes.membership_name,
                status,
                freezeDatetime,
                cancellationDate,
                endDate,
                isEndDateFuture: endDate ? new Date(endDate) > now : 'no end date',
                isCancellationBeforeEnd: cancellationDate && endDate ? new Date(cancellationDate) < new Date(endDate) : 'n/a'
              })

              // Show if status is "active" or "frozen"
              if (status === 'active' || status === 'frozen') return true

              // Fallback logic for memberships without status field:
              // Active if has an end_date in the future AND not cancelled (or cancellation is before end date)
              if (!endDate) return false // No end date and no active status = expired/invalid

              const endDateObj = new Date(endDate)
              if (endDateObj <= now) return false // End date in past = expired

              // If no cancellation, it's active
              if (!cancellationDate) return true

              // If cancelled but cancellation is before end date, still active
              return new Date(cancellationDate) < endDateObj
            })

            console.log('Active memberships:', activeMemberships.map((m: any) => ({ id: m.id, name: m.attributes.membership_name })))

            fetchedMemberships = activeMemberships

            // Demo mode: For j.kliman@barrys.com, add demo membership if none exist
            if (userData?.user?.email === 'j.kliman@barrys.com' && activeMemberships.length === 0) {
              console.log('🎬 Demo Mode: Adding demo membership for j.kliman@barrys.com')
              const demoMembership = {
                id: 'demo-membership-1',
                attributes: {
                  membership_name: '8 Classes/Month Recurring Membership',
                  status: 'active',
                  start_datetime: '2024-06-01T00:00:00Z',
                  end_date: '2025-06-01T00:00:00Z',
                  freeze_datetime: null,
                  cancellation_datetime: null
                }
              }
              fetchedMemberships = [demoMembership]
            }

            setMemberships(fetchedMemberships)
          } catch (error) {
            console.error('Failed to fetch memberships:', error)
            setMemberships([])
          } finally {
            setLoadingStates(prev => ({ ...prev, memberships: false }))
            setCompletedStates(prev => ({ ...prev, memberships: true }))
          }
        } else {
          setCompletedStates(prev => ({ ...prev, memberships: true }))
        }

        // Fetch class packages if we have a Mariana Tek user ID
        if (userData.user.external_id) {
          setLoadingStates(prev => ({ ...prev, packages: true }))
          try {
            // Fetch credit transactions with 15 second timeout (longer due to processing)
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Credit transactions lookup timeout')), 15000)
            )
            const creditTransactionsData = await Promise.race([
              getMarianaTekCreditTransactions(userData.user.external_id),
              timeoutPromise
            ]) as any
            console.log('Credit transactions data received:', creditTransactionsData)

            // Group transactions by credit name
            const packageMap = new Map<string, any>()

            const transactions = creditTransactionsData.data || []

            // First pass: identify active package ROOT transactions
            const activePackageIds = new Set<string>()

            for (const transaction of transactions) {
              const creditName = transaction.attributes.credit_name
              const remainingCreditsCache = transaction.attributes.remaining_credits_cache
              const amount = transaction.attributes.transaction_amount || 0
              const parentTxData = transaction.relationships?.parent_credit_transaction?.data
              const isRootTransaction = !parentTxData

              // Only look for ROOT transactions of class packages
              const isClassPackage = creditName && (
                creditName.toLowerCase().includes('classes') ||
                creditName.toLowerCase().includes('holiday sale')
              )

              if (!isClassPackage || !isRootTransaction) continue

              // Check if package name contains an old year
              const currentYear = new Date().getFullYear()
              const yearMatch = creditName.match(/\b(20\d{2})\b/)
              if (yearMatch) {
                const packageYear = parseInt(yearMatch[1])
                if (packageYear < currentYear - 1) continue
              }

              // ROOT transactions with remaining_credits_cache > 0 are active packages
              if (remainingCreditsCache !== null && remainingCreditsCache > 0) {
                activePackageIds.add(transaction.id)
              }
            }

            console.log('Active package IDs:', Array.from(activePackageIds))

            // Second pass: process all transactions for active packages
            for (const transaction of transactions) {
              const creditName = transaction.attributes.credit_name
              const expirationDate = transaction.attributes.expiration_datetime
              const amount = transaction.attributes.transaction_amount || 0
              const parentTxData = transaction.relationships?.parent_credit_transaction?.data
              const parentTxId = parentTxData?.id
              const isRootTransaction = !parentTxData

              // Only process transactions with "classes" or "holiday sale" in the credit name
              const isClassPackage = creditName && (
                creditName.toLowerCase().includes('classes') ||
                creditName.toLowerCase().includes('holiday sale')
              )

              if (!isClassPackage) continue

              // Only process transactions belonging to active packages
              const belongsToActivePackage = isRootTransaction
                ? activePackageIds.has(transaction.id)
                : activePackageIds.has(parentTxId || '')

              if (!belongsToActivePackage) continue

              // Check if package name contains an old year (e.g., "Holiday Sale 2023")
              // and filter it out as expired (exclude packages 2+ years old)
              const currentYear = new Date().getFullYear()
              const yearMatch = creditName.match(/\b(20\d{2})\b/)
              if (yearMatch) {
                const packageYear = parseInt(yearMatch[1])
                // Allow current year and previous year, exclude older packages
                if (packageYear < currentYear - 1) continue
              }

              // Determine the package ID for this transaction
              const packageId = isRootTransaction ? transaction.id : (parentTxId || '')

              // Get or create package entry (keyed by package ID)
              if (!packageMap.has(packageId)) {
                // Extract total classes from package name
                const numbers = creditName.match(/\d+/g) || []
                let packageSize = 0

                for (const num of numbers) {
                  const numValue = parseInt(num)
                  // Skip years (typically 2000-2099)
                  if (numValue < 2000 || numValue > 2099) {
                    packageSize = numValue
                    break
                  }
                }

                packageMap.set(packageId, {
                  id: packageId,
                  name: creditName,
                  packageSize: packageSize,
                  totalPurchased: 0,
                  totalUsed: 0,
                  purchaseDate: null,
                  expirationDate: null
                })
              }

              const pkg = packageMap.get(packageId)

              // Identify purchase transactions (ROOT with positive amount)
              if (isRootTransaction && amount > 0) {
                pkg.totalPurchased = amount
                pkg.purchaseDate = transaction.attributes.transaction_datetime
                pkg.expirationDate = expirationDate
              } else if (!isRootTransaction) {
                // All child transactions (usage and refunds)
                // Negative = usage, Positive = refund
                pkg.totalUsed -= amount // Subtract (negative becomes positive addition, positive becomes negative subtraction)
              }
            }

            // Filter to only show packages with remaining classes and not expired
            const packagesWithRemaining = Array.from(packageMap.values()).filter(pkg => {
              const remaining = pkg.totalPurchased - pkg.totalUsed
              if (remaining <= 0) return false

              // Check if expired
              if (pkg.expirationDate) {
                const expiration = new Date(pkg.expirationDate)
                const now = new Date()
                if (expiration < now) return false
              }

              return true
            }).map(pkg => ({
              id: pkg.id,
              name: pkg.name,
              totalClasses: pkg.totalPurchased,
              usedClasses: pkg.totalUsed,
              purchaseDate: pkg.purchaseDate,
              expirationDate: pkg.expirationDate
            }))

            fetchedClassPackages = packagesWithRemaining

            // Build the Credits list: any active root credit transaction with
            // remaining_credits_cache > 0 that ISN'T a class package (so we don't
            // duplicate them in the Class Packages section).
            const creditList: any[] = []
            for (const transaction of transactions) {
              const creditName = transaction.attributes.credit_name
              const remainingCreditsCache = transaction.attributes.remaining_credits_cache
              const expirationDate = transaction.attributes.expiration_datetime
              const transactionDate = transaction.attributes.transaction_datetime || transaction.attributes.created_at
              const parentTxData = transaction.relationships?.parent_credit_transaction?.data
              const isRootTransaction = !parentTxData
              if (!isRootTransaction) continue
              if (!creditName) continue
              if (remainingCreditsCache == null || remainingCreditsCache <= 0) continue

              const isClassPackage =
                creditName.toLowerCase().includes('classes') ||
                creditName.toLowerCase().includes('holiday sale')
              if (isClassPackage) continue

              creditList.push({
                id: transaction.id,
                name: creditName,
                remaining: remainingCreditsCache,
                purchaseDate: transactionDate,
                expirationDate
              })
            }
            creditList.sort((a, b) =>
              (b.purchaseDate || '').localeCompare(a.purchaseDate || '')
            )
            setCredits(creditList)

            // Demo mode: For j.kliman@barrys.com, add demo packages if none exist
            if (userData?.user?.email === 'j.kliman@barrys.com' && packagesWithRemaining.length === 0) {
              console.log('🎬 Demo Mode: Adding demo packages for j.kliman@barrys.com')
              const demoPackages = [
                {
                  id: 'demo-package-1',
                  name: 'NYC 10 Classes',
                  totalClasses: 10,
                  usedClasses: 3,
                  purchaseDate: '2024-12-15T00:00:00Z',
                  expirationDate: '2025-12-15T00:00:00Z'
                },
                {
                  id: 'demo-package-2',
                  name: 'Holiday Sale 2024 - 5 Classes',
                  totalClasses: 5,
                  usedClasses: 1,
                  purchaseDate: '2024-11-28T00:00:00Z',
                  expirationDate: '2025-11-28T00:00:00Z'
                }
              ]
              fetchedClassPackages = demoPackages
            }

            setClassPackages(fetchedClassPackages)
          } catch (error) {
            console.error('Failed to fetch class packages:', error)
            setClassPackages([])
          } finally {
            setLoadingStates(prev => ({ ...prev, packages: false }))
            setCompletedStates(prev => ({ ...prev, packages: true }))
          }
        } else {
          setCompletedStates(prev => ({ ...prev, packages: true }))
        }

        // Fetch reservations if we have a Mariana Tek user ID (last 4 months)
        if (userData.user.external_id) {
          setLoadingStates(prev => ({ ...prev, reservations: true }))
          try {
            // Fetch reservations with 15 second timeout (longer due to 4 months of data)
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Reservations lookup timeout')), 15000)
            )
            const reservationsData = await Promise.race([
              getMarianaTekReservations(userData.user.external_id, 4),
              timeoutPromise
            ]) as any
            console.log('Reservations data received:', reservationsData)
            fetchedReservations = reservationsData
            setReservations(reservationsData)
          } catch (error) {
            console.error('Failed to fetch reservations:', error)
            setReservations(null)
          } finally {
            setLoadingStates(prev => ({ ...prev, reservations: false }))
            setCompletedStates(prev => ({ ...prev, reservations: true }))
          }
        } else {
          setCompletedStates(prev => ({ ...prev, reservations: true }))
        }

        // Fetch Extole referral code
        let fetchedReferralCode: string | null = null
        setLoadingStates(prev => ({ ...prev, extole: true }))
        try {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Extole lookup timeout')), 10000)
          )
          const extoleData = await Promise.race([
            getExtolePersonByEmail(userData.user.email),
            timeoutPromise
          ]) as any
          console.log('Extole person data received:', extoleData)

          if (extoleData && extoleData.length > 0) {
            const personId = extoleData[0].id
            // Fetch shareables to get the referral code
            const shareablesData = await getExtoleShareables(personId)
            console.log('Extole shareables data received:', shareablesData)

            if (shareablesData && shareablesData.length > 0) {
              // Get the code from the first shareable (usually refer-a-friend)
              fetchedReferralCode = shareablesData[0].code || null
              setReferralCode(fetchedReferralCode)
            }
          }
        } catch (error) {
          console.error('Failed to fetch Extole data:', error)
          setReferralCode(null)
        } finally {
          setLoadingStates(prev => ({ ...prev, extole: false }))
          setCompletedStates(prev => ({ ...prev, extole: true }))
        }

        // Cache all the fetched data
        cache.set(userData.user.email, {
          marianaTekData: fetchedMarianaTekData,
          barrysPlatformData: fetchedBarrysPlatformData,
          shopifyData: fetchedShopifyData,
          memberships: fetchedMemberships,
          classPackages: fetchedClassPackages,
          credits,
          reservations: fetchedReservations,
          referralCode: fetchedReferralCode
        })

        // Notify parent of external data
        if (onExternalDataLoaded) {
          onExternalDataLoaded(fetchedMarianaTekData, fetchedBarrysPlatformData, fetchedReservations)
        }

        // Mark initial load as complete
        setIsInitialLoad(false)
      } catch (error) {
        console.error('Error fetching external data:', error)
        setIsInitialLoad(false)
      } finally {
        setIsFetching(false)
        fetchingRef.current = false
      }
    }

    fetchExternalData()
  }, [userData?.user?.email, refreshTrigger, isAdmin, isSuperAdmin])

  // Calculate loading state
  const totalSteps = 7 // marianaTek, barrysPlatform, shopify, memberships, packages, reservations, extole
  const completedCount = Object.values(completedStates).filter(Boolean).length
  const isAnyLoading = Object.values(loadingStates).some(Boolean)
  const isAllDataLoaded = completedCount === totalSteps && !isAnyLoading
  const showLoadingSpinner = !isInitialLoad && !isAllDataLoaded

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(showLoadingSpinner)
    }
  }, [showLoadingSpinner, onLoadingStateChange])

  if (!userData || !userData.user) {
    return null
  }

  const user = userData.user
  const profile = user.user_profile || {}

  // Extract completed class count from Mariana Tek data
  const completedClassCount = marianaTekData?.data?.[0]?.attributes?.completed_class_count || 0

  // Extract tags from Mariana Tek data
  const userTags = marianaTekData?.data?.[0]?.relationships?.tags?.data || []

  console.log('Completed class count:', completedClassCount)
  console.log('User tags:', userTags)

  const handleTagHover = (tagId: number, event: React.MouseEvent) => {
    setHoveredTag(tagId)
    const rect = event.currentTarget.getBoundingClientRect()
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    })
  }

  const handleTagLeave = () => {
    setHoveredTag(null)
  }

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      console.log(`${label} copied to clipboard: ${text}`)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      console.log(`${label} copied to clipboard (fallback): ${text}`)
    }
  }

  const formatAddress = () => {
    const parts = []
    if (user.address) parts.push(user.address)
    if (user.city && user.state && user.zip) {
      parts.push(`${user.city}, ${user.state} ${user.zip}`)
    } else {
      if (user.city) parts.push(user.city)
      if (user.state) parts.push(user.state)
      if (user.zip) parts.push(user.zip)
    }
    return parts.join('\n')
  }

  const getPhoneNumber = () => {
    if (user.phone_numbers && user.phone_numbers.length > 0) {
      return user.phone_numbers[0].phone_number
    }
    return 'N/A'
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    try {
      // Parse date string without timezone conversion to avoid off-by-one errors
      // Date strings like "1981-03-13" should display as March 13, not March 12
      const parts = dateString.split('-')
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10)
        const month = parseInt(parts[1], 10) - 1 // JS months are 0-indexed
        const day = parseInt(parts[2], 10)
        return new Date(year, month, day).toLocaleDateString()
      }
      // Fallback for other formats
      return new Date(dateString).toLocaleDateString()
    } catch {
      return dateString
    }
  }

  // Show loading spinner if data is still loading
  if (showLoadingSpinner) {
    return (
      <LoadingOverlay>
        <LoadingSpinner />
        <LoadingText>Loading user profile data...</LoadingText>
      </LoadingOverlay>
    )
  }

  return (
    <ProfileContainer>
      
      <ProfileHeader>
        <ProfileHeaderInfo>
          <Title>User Profile</Title>
        </ProfileHeaderInfo>
        <TagsAndClassesContainer>
          <TagsContainer>
            {userTags.map((tag: any) => {
              const tagId = parseInt(tag.id)
              const tagInfo = TAG_MAPPING[tagId]
              if (!tagInfo) return null
              
              return (
                <TagIconWrapper key={tagId}>
                  <TagIcon
                    src={`/img/${tagInfo.icon}`}
                    alt={tagInfo.description}
                    onMouseEnter={(e) => handleTagHover(tagId, e)}
                    onMouseLeave={handleTagLeave}
                    onError={(e) => {
                      console.error(`Failed to load tag icon: ${tagInfo.icon}`)
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                  {hoveredTag === tagId && (
                    <Tooltip 
                      show={true}
                      style={{
                        left: tooltipPosition.x,
                        top: tooltipPosition.y,
                        position: 'fixed'
                      }}
                    >
                      {tagInfo.description}
                    </Tooltip>
                  )}
                </TagIconWrapper>
              )
            })}
          </TagsContainer>
          <ClassesTaken>
            Classes Taken: {completedClassCount}
          </ClassesTaken>
        </TagsAndClassesContainer>
      </ProfileHeader>
      <ProfileGrid>
        <ProfileField>
          <Label>First Name</Label>
          <Value>{user.first_name || 'N/A'}</Value>
        </ProfileField>

        <ProfileField>
          <Label>Last Name</Label>
          <Value>{user.last_name || 'N/A'}</Value>
        </ProfileField>

        <ProfileField>
          <Label>Email Address</Label>
          <ValueWithCopy>
            <Value>{user.email || 'N/A'}</Value>
            {user.email && (
              <CopyButton onClick={() => copyToClipboard(user.email, 'Email')}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </CopyButton>
            )}
          </ValueWithCopy>
        </ProfileField>

        <ProfileField>
          <Label>SessionM ID</Label>
          <ValueWithCopy>
            <Value>{user.id || 'N/A'}</Value>
            {user.id && (
              <CopyButton onClick={() => copyToClipboard(user.id, 'SessionM ID')}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </CopyButton>
            )}
          </ValueWithCopy>
        </ProfileField>

        <ProfileField>
          <Label>Mariana Tek ID (External ID)</Label>
          <ValueWithCopy>
            <Value>{user.external_id || 'N/A'}</Value>
            {user.external_id && (
              <CopyButton onClick={() => copyToClipboard(user.external_id, 'Mariana Tek ID')}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </CopyButton>
            )}
          </ValueWithCopy>
        </ProfileField>

        <ProfileField>
          <Label>Barry's Platform ID</Label>
          <ValueWithCopy>
            <Value>{barrysPlatformData?.found ? barrysPlatformData.id : 'N/A'}</Value>
            {barrysPlatformData?.found && barrysPlatformData.id && (
              <CopyButton onClick={() => copyToClipboard(barrysPlatformData.id, "Barry's Platform ID")}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </CopyButton>
            )}
          </ValueWithCopy>
        </ProfileField>

        <ProfileField>
          <Label>Shopify ID</Label>
          <ValueWithCopy>
            <Value>{shopifyData?.found ? shopifyData.id : 'N/A'}</Value>
            {shopifyData?.found && shopifyData.id && (
              <CopyButton onClick={() => copyToClipboard(shopifyData.id, 'Shopify ID')}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </CopyButton>
            )}
          </ValueWithCopy>
        </ProfileField>

        <ProfileField>
          <Label>Referral Code</Label>
          <ValueWithCopy>
            <Value>{referralCode || 'N/A'}</Value>
            {referralCode && (
              <CopyButton onClick={() => copyToClipboard(referralCode, 'Referral Code')}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </CopyButton>
            )}
          </ValueWithCopy>
        </ProfileField>

        <ProfileField>
          <Label>Date of Birth</Label>
          <Value>{formatDate(user.dob)}</Value>
        </ProfileField>

        <ProfileField>
          <Label>Phone Number</Label>
          <Value>{getPhoneNumber()}</Value>
        </ProfileField>

        <AddressContainer>
          <ProfileField>
            <Label>Address</Label>
            <AddressValue>{formatAddress() || 'N/A'}</AddressValue>
          </ProfileField>

          {isSuperAdmin && (
            <ProfileLinksContainer>
              <ProfileLinksTitle>Profile Links</ProfileLinksTitle>
              <LinksGrid>
                <LinkButton
                  onClick={() => window.open(`https://mmc-barrys.ent-sessionm.com/customers2/${user.id}/profile`, '_blank')}
                  disabled={!user.id}
                >
                  SessionM
                </LinkButton>

                <LinkButton
                  onClick={() => window.open(`https://barrysbootcamp.marianatek.com/admin/user/profile/${user.external_id}/account`, '_blank')}
                  disabled={!user.external_id}
                >
                  Mariana Tek
                </LinkButton>

                <LinkButton
                  onClick={() => window.open(`https://platform.barrys.com/admin/users/${barrysPlatformData?.id}`, '_blank')}
                  disabled={!barrysPlatformData?.found || !barrysPlatformData?.id}
                >
                  Middleware
                </LinkButton>

                <LinkButton
                  onClick={() => window.open(`https://admin.shopify.com/store/barrys-bootcamp-shop/customers/${shopifyData?.id}`, '_blank')}
                  disabled={!shopifyData?.found || !shopifyData?.id}
                >
                  Shopify
                </LinkButton>
              </LinksGrid>
            </ProfileLinksContainer>
          )}

          {marianaTekData && (
            <>
              <UserTagManagement
                marianaTekData={marianaTekData}
                onUpdate={onUpdate}
              />
              <UserNotesButton onClick={() => setShowUserNotes(true)}>
                User Notes
              </UserNotesButton>
            </>
          )}
        </AddressContainer>

        <MembershipsSection>
          <MembershipsTitle>Current Memberships (Active & Frozen)</MembershipsTitle>
          {loadingStates.memberships ? (
            <SectionLoadingContainer>
              <SectionLoadingText>Section Loading...</SectionLoadingText>
            </SectionLoadingContainer>
          ) : memberships.length === 0 ? (
            <NoMemberships>No active memberships</NoMemberships>
          ) : (
            <MembershipsList>
              {memberships.map((membership: any) => (
                <MembershipCard key={membership.id}>
                  <MembershipName>
                    {membership.attributes.membership_name}
                    {membership.attributes.status === 'frozen' && ' (FROZEN)'}
                  </MembershipName>
                  <MembershipDetail>
                    <MembershipLabel>Status:</MembershipLabel>
                    <MembershipValue style={{ color: membership.attributes.status === 'frozen' ? '#0088cc' : '#2e7d32' }}>
                      {membership.attributes.status?.toUpperCase() || 'ACTIVE'}
                    </MembershipValue>
                  </MembershipDetail>
                  <MembershipDetail>
                    <MembershipLabel>Classes Remaining:</MembershipLabel>
                    <MembershipValue>
                      {membership.attributes.last_interval_remaining_usage_count !== null
                        ? membership.attributes.last_interval_remaining_usage_count
                        : 'Unlimited'}
                    </MembershipValue>
                  </MembershipDetail>
                  <MembershipDetail>
                    <MembershipLabel>Purchase Date:</MembershipLabel>
                    <MembershipValue>
                      {new Date(membership.attributes.purchase_date).toLocaleDateString()}
                    </MembershipValue>
                  </MembershipDetail>
                  {membership.attributes.freeze_datetime && (
                    <MembershipDetail>
                      <MembershipLabel>Frozen Since:</MembershipLabel>
                      <MembershipValue>
                        {new Date(membership.attributes.freeze_datetime).toLocaleDateString()}
                      </MembershipValue>
                    </MembershipDetail>
                  )}
                  {membership.attributes.freeze_reactivation_datetime && (
                    <MembershipDetail>
                      <MembershipLabel>Reactivation Date:</MembershipLabel>
                      <MembershipValue>
                        {new Date(membership.attributes.freeze_reactivation_datetime).toLocaleDateString()}
                      </MembershipValue>
                    </MembershipDetail>
                  )}
                  {membership.attributes.next_charge_date && (
                    <MembershipDetail>
                      <MembershipLabel>Next Charge:</MembershipLabel>
                      <MembershipValue>
                        {new Date(membership.attributes.next_charge_date).toLocaleDateString()}
                      </MembershipValue>
                    </MembershipDetail>
                  )}
                </MembershipCard>
              ))}
            </MembershipsList>
          )}
        </MembershipsSection>

        <PackagesSection>
          <PackagesTitle>Class Packages</PackagesTitle>
          {loadingStates.packages ? (
            <SectionLoadingContainer>
              <SectionLoadingText>Section Loading...</SectionLoadingText>
            </SectionLoadingContainer>
          ) : classPackages.length === 0 ? (
            <NoPackages>No class packages with remaining classes</NoPackages>
          ) : (
            <PackagesList>
              {classPackages.map((pkg: any) => {
                const remaining = pkg.totalClasses - pkg.usedClasses
                return (
                  <PackageCard key={pkg.id}>
                    <PackageName>{pkg.name}</PackageName>
                    <PackageDetail>
                      <PackageLabel>Classes Remaining:</PackageLabel>
                      <PackageValue>{remaining} / {pkg.totalClasses}</PackageValue>
                    </PackageDetail>
                    <PackageDetail>
                      <PackageLabel>Purchase Date:</PackageLabel>
                      <PackageValue>
                        {new Date(pkg.purchaseDate).toLocaleDateString()}
                      </PackageValue>
                    </PackageDetail>
                    {pkg.expirationDate && (
                      <PackageDetail>
                        <PackageLabel>Expiration Date:</PackageLabel>
                        <PackageValue>
                          {new Date(pkg.expirationDate).toLocaleDateString()}
                        </PackageValue>
                      </PackageDetail>
                    )}
                  </PackageCard>
                )
              })}
            </PackagesList>
          )}
        </PackagesSection>

        <PackagesSection>
          <PackagesTitle>Credits</PackagesTitle>
          {loadingStates.packages ? (
            <SectionLoadingContainer>
              <SectionLoadingText>Section Loading...</SectionLoadingText>
            </SectionLoadingContainer>
          ) : credits.length === 0 ? (
            <NoPackages>No active credits</NoPackages>
          ) : (
            <PackagesList>
              {credits.map((credit: any) => (
                <PackageCard key={credit.id}>
                  <PackageName>{credit.name}</PackageName>
                  <PackageDetail>
                    <PackageLabel>Remaining:</PackageLabel>
                    <PackageValue>{credit.remaining}</PackageValue>
                  </PackageDetail>
                  {credit.purchaseDate && (
                    <PackageDetail>
                      <PackageLabel>Issued:</PackageLabel>
                      <PackageValue>{new Date(credit.purchaseDate).toLocaleDateString()}</PackageValue>
                    </PackageDetail>
                  )}
                  {credit.expirationDate && (
                    <PackageDetail>
                      <PackageLabel>Expiration Date:</PackageLabel>
                      <PackageValue>{new Date(credit.expirationDate).toLocaleDateString()}</PackageValue>
                    </PackageDetail>
                  )}
                </PackageCard>
              ))}
            </PackagesList>
          )}
        </PackagesSection>

        {isSuperAdmin && (
          <MergedProfilesSection>
            <MergedProfilesTitle>Merged Profile Backups</MergedProfilesTitle>
            {loadingBackups ? (
              <SectionLoadingContainer>
                <SectionLoadingText>Checking for merged profile data...</SectionLoadingText>
              </SectionLoadingContainer>
            ) : mergedProfileBackups.length === 0 ? (
              <NoBackups>No merged profile backups found for this user</NoBackups>
            ) : (
              <BackupsList>
                {mergedProfileBackups.map((backup: any) => (
                  <BackupCard key={backup.key}>
                    <BackupInfo>
                      <BackupEmail>{backup.email}</BackupEmail>
                      <BackupDate>
                        Merged: {backup.captured_at ? new Date(backup.captured_at).toLocaleString() : 'Unknown'}
                      </BackupDate>
                    </BackupInfo>
                    <DownloadButton
                      onClick={() => handleDownloadExcel(backup.key)}
                      disabled={downloadingKey === backup.key}
                    >
                      {downloadingKey === backup.key ? (
                        'Downloading...'
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                          </svg>
                          Download Excel
                        </>
                      )}
                    </DownloadButton>
                  </BackupCard>
                ))}
              </BackupsList>
            )}
          </MergedProfilesSection>
        )}
      </ProfileGrid>

      <UserNotesModal
        isOpen={showUserNotes}
        onClose={() => setShowUserNotes(false)}
        userData={userData}
      />
    </ProfileContainer>
  )
}

export default UserProfile
